import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart3,
  ShoppingBag,
  Package,
  Tag,
  ArrowLeft,
  RefreshCw,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { useProducts } from '../context/ProductContext';
import { toast } from 'sonner';
import { AnalyticsTab } from '../components/admin/AnalyticsTab';
import { OrdersTab } from '../components/admin/OrdersTab';
import { ProductsTab } from '../components/admin/ProductsTab';
import { DiscountsTab } from '../components/admin/DiscountsTab';
import { ProductEditModal } from '../components/admin/ProductEditModal';
import { DiscountFormModal } from '../components/admin/DiscountFormModal';
import { OrderFulfillmentModal } from '../components/admin/OrderFulfillmentModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SHOP_OWNER_EMAIL = 'daizyinfused@gmail.com';

const TABS = [
  { id: 'analytics', label: 'Analytics', icon: BarChart3 },
  { id: 'orders', label: 'Orders', icon: ShoppingBag },
  { id: 'products', label: 'Products', icon: Package },
  { id: 'discounts', label: 'Discounts', icon: Tag },
];

const EMPTY_DISCOUNT_FORM = {
  code: '',
  discount_type: 'percentage',
  value: '',
  min_order_amount: '',
  max_uses: '',
  expires_at: '',
};

const AdminDashboard = () => {
  const { user, token, isAuthenticated, loading } = useAuth();
  const { products, refreshProducts } = useProducts();
  const navigate = useNavigate();

  const [analytics, setAnalytics] = useState(null);
  const [orders, setOrders] = useState(null);
  const [allOrders, setAllOrders] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [selectedDays, setSelectedDays] = useState(30);
  const [activeTab, setActiveTab] = useState('analytics');

  // Product editing
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({});
  const [savingProduct, setSavingProduct] = useState(false);

  // Discount form
  const [showDiscountForm, setShowDiscountForm] = useState(false);
  const [discountForm, setDiscountForm] = useState(EMPTY_DISCOUNT_FORM);

  // Order fulfillment
  const [editingOrder, setEditingOrder] = useState(null);
  const [fulfillmentForm, setFulfillmentForm] = useState({
    status: 'pending',
    tracking_number: '',
    carrier: '',
    notes: '',
    send_notification: true,
  });
  const [updatingOrder, setUpdatingOrder] = useState(false);

  const isShopOwner = user?.email?.toLowerCase() === SHOP_OWNER_EMAIL.toLowerCase();

  const fetchAllData = useCallback(async () => {
    setLoadingData(true);
    try {
      const [analyticsRes, ordersRes, allOrdersRes, discountsRes] = await Promise.all([
        fetch(`${API_URL}/api/analytics/dashboard?days=${selectedDays}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API_URL}/api/analytics/orders?days=${selectedDays}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API_URL}/api/orders/`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API_URL}/api/discounts`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      if (analyticsRes.ok) setAnalytics(await analyticsRes.json());
      if (ordersRes.ok) setOrders(await ordersRes.json());
      if (allOrdersRes.ok) setAllOrders(await allOrdersRes.json());
      if (discountsRes.ok) setDiscounts(await discountsRes.json());
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoadingData(false);
    }
  }, [selectedDays, token]);

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/auth');
      return;
    }

    if (!loading && isAuthenticated && !isShopOwner) {
      toast.error('Access denied. Shop owner only.');
      navigate('/account');
      return;
    }

    if (isAuthenticated && isShopOwner) {
      fetchAllData();
    }
  }, [loading, isAuthenticated, isShopOwner, navigate, fetchAllData]);

  // Product handlers
  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      ...product,
      sizes: product.sizes.map((s) => ({ ...s })),
      images: [...(product.images || [])],
      ingredients: product.ingredients || [],
      benefits: product.benefits || [],
    });
  };

  const handleNewProduct = () => {
    // Use a sentinel id=0 so handleSaveProduct knows to POST (create) instead of PUT (update)
    setEditingProduct({ id: 0, name: 'New Product' });
    setProductForm({
      id: 0,
      name: '',
      basePrice: 0,
      category: 'Intention Oils',
      image: '',
      images: [],
      sizes: [{ size: '2oz Bottle', price: 0, quantity: 0, image: '' }],
      description: '',
      ingredients: [],
      benefits: [],
      usage: '',
    });
  };

  const handleDeleteProduct = async (product) => {
    if (!window.confirm(`Delete "${product.name}"? This cannot be undone.`)) return;
    try {
      const response = await fetch(`${API_URL}/api/products/${product.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.status === 204 || response.ok) {
        toast.success('Product deleted');
        refreshProducts();
      } else {
        toast.error('Failed to delete product');
      }
    } catch (error) {
      toast.error('Error deleting product');
    }
  };

  const handleSaveProduct = async () => {
    setSavingProduct(true);
    const isNew = !editingProduct.id || editingProduct.id === 0;

    // Validate minimal fields for new products
    if (isNew) {
      if (!productForm.name?.trim() || !productForm.basePrice || !productForm.image?.trim()) {
        toast.error('Name, base price, and main image are required');
        setSavingProduct(false);
        return;
      }
    }

    const body = {
      name: productForm.name,
      basePrice: parseFloat(productForm.basePrice) || 0,
      category: productForm.category,
      image: productForm.image,
      images: (productForm.images || []).filter((img) => img && img.trim() !== ''),
      sizes: productForm.sizes,
      description: productForm.description,
      ingredients: productForm.ingredients,
      benefits: productForm.benefits,
      usage: productForm.usage,
    };

    try {
      const url = isNew
        ? `${API_URL}/api/products`
        : `${API_URL}/api/products/${editingProduct.id}`;
      const method = isNew ? 'POST' : 'PUT';

      // POST requires the full ProductCreate schema (including id + images at least one)
      const finalBody = isNew
        ? {
            ...body,
            id: 0, // backend auto-assigns next id
            images: body.images.length > 0 ? body.images : [body.image],
          }
        : body;

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(finalBody),
      });

      if (response.ok) {
        toast.success(isNew ? 'Product created successfully!' : 'Product updated successfully!');
        setEditingProduct(null);
        refreshProducts();
      } else {
        const error = await response.json();
        toast.error(error.detail || `Failed to ${isNew ? 'create' : 'update'} product`);
      }
    } catch (error) {
      toast.error(`Error ${isNew ? 'creating' : 'updating'} product`);
    } finally {
      setSavingProduct(false);
    }
  };

  // Discount handlers
  const handleCreateDiscount = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/api/discounts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...discountForm,
          value: parseFloat(discountForm.value),
          min_order_amount: discountForm.min_order_amount
            ? parseFloat(discountForm.min_order_amount)
            : 0,
          max_uses: discountForm.max_uses ? parseInt(discountForm.max_uses) : null,
          expires_at: discountForm.expires_at || null,
        }),
      });

      if (response.ok) {
        toast.success('Discount code created!');
        setShowDiscountForm(false);
        setDiscountForm(EMPTY_DISCOUNT_FORM);
        fetchAllData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create discount');
      }
    } catch (error) {
      toast.error('Error creating discount');
    }
  };

  const handleDeleteDiscount = async (code) => {
    if (!window.confirm('Are you sure you want to delete this discount?')) return;
    try {
      await fetch(`${API_URL}/api/discounts/${code}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Discount deleted');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete discount');
    }
  };

  const handleToggleDiscount = async (code) => {
    try {
      await fetch(`${API_URL}/api/discounts/${code}/toggle`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Discount status updated');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to toggle discount');
    }
  };

  // Order handlers
  const handleEditOrder = (order) => {
    setEditingOrder(order);
    setFulfillmentForm({
      status: order.status || 'pending',
      tracking_number: order.tracking_number || '',
      carrier: order.carrier || '',
      notes: order.fulfillment_notes || '',
      send_notification: true,
    });
  };

  const handleUpdateFulfillment = async () => {
    setUpdatingOrder(true);
    try {
      const response = await fetch(
        `${API_URL}/api/orders/${editingOrder.order_number}/fulfillment`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(fulfillmentForm),
        }
      );

      if (response.ok) {
        const result = await response.json();
        if (result.email_notification?.sent) {
          toast.success('Order updated and customer notified by email!');
        } else {
          toast.success('Order updated!');
        }
        setEditingOrder(null);
        fetchAllData();
      } else {
        toast.error('Failed to update order');
      }
    } catch (error) {
      toast.error('Error updating order');
    } finally {
      setUpdatingOrder(false);
    }
  };

  if (loading || loadingData) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-950 to-purple-900 flex items-center justify-center">
        <RefreshCw className="w-12 h-12 text-yellow-400 animate-spin" />
      </div>
    );
  }

  if (!isShopOwner) return null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-950 via-purple-900 to-purple-950 py-20">
      <div className="container mx-auto px-4">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-8 gap-4">
          <div>
            <Button
              variant="ghost"
              onClick={() => navigate('/account')}
              className="text-purple-300 hover:text-white mb-2"
              data-testid="back-to-account-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Account
            </Button>
            <h1 className="text-3xl md:text-4xl font-bold text-white flex items-center gap-3">
              <BarChart3 className="w-8 md:w-10 h-8 md:h-10 text-yellow-400" />
              Admin Dashboard
            </h1>
          </div>

          <div className="flex items-center gap-4">
            <Button
              onClick={fetchAllData}
              variant="outline"
              className="border-purple-600 text-purple-200"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <select
              value={selectedDays}
              onChange={(e) => setSelectedDays(parseInt(e.target.value))}
              className="bg-purple-800 border border-purple-600 text-white rounded-lg px-4 py-2"
              data-testid="days-select"
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
          </div>
        </div>

        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <Button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-yellow-500 text-purple-900'
                    : 'bg-purple-800/50 text-purple-200 hover:bg-purple-700'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <Icon className="w-4 h-4 mr-2" />
                {tab.label}
              </Button>
            );
          })}
        </div>

        {activeTab === 'analytics' && <AnalyticsTab analytics={analytics} orders={orders} />}
        {activeTab === 'orders' && <OrdersTab orders={allOrders} onEditOrder={handleEditOrder} />}
        {activeTab === 'products' && (
          <ProductsTab
            products={products}
            onEditProduct={handleEditProduct}
            onNewProduct={handleNewProduct}
            onDeleteProduct={handleDeleteProduct}
          />
        )}
        {activeTab === 'discounts' && (
          <DiscountsTab
            discounts={discounts}
            onNewDiscount={() => setShowDiscountForm(true)}
            onToggle={handleToggleDiscount}
            onDelete={handleDeleteDiscount}
          />
        )}

        {editingProduct && (
          <ProductEditModal
            product={editingProduct}
            form={productForm}
            setForm={setProductForm}
            saving={savingProduct}
            onClose={() => setEditingProduct(null)}
            onSave={handleSaveProduct}
          />
        )}

        {showDiscountForm && (
          <DiscountFormModal
            form={discountForm}
            setForm={setDiscountForm}
            onClose={() => setShowDiscountForm(false)}
            onSubmit={handleCreateDiscount}
          />
        )}

        {editingOrder && (
          <OrderFulfillmentModal
            order={editingOrder}
            form={fulfillmentForm}
            setForm={setFulfillmentForm}
            updating={updatingOrder}
            onClose={() => setEditingOrder(null)}
            onSubmit={handleUpdateFulfillment}
          />
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
